import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Gauge } from 'lucide-react';
import { useVisibility } from '@/hooks/useVisibility';
import { useUsageSummary } from '@/hooks/useUsageSummary';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';

const DISMISS_KEY = 'usage-alert-dismissed';

/**
 * In-app notice when this month's call-tracking usage has passed the dollar
 * alert the tenant set. Admins only; dismissible per month per device.
 */
export function UsageAlertBanner() {
  const { canManageSettings } = useVisibility();
  const { summary } = useUsageSummary(canManageSettings);
  const { locale } = useCompanyLocale();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState<string | null>(null);

  useEffect(() => {
    try {
      setDismissed(localStorage.getItem(DISMISS_KEY));
    } catch {
      setDismissed(null);
    }
  }, []);

  if (!canManageSettings || !summary || summary.ctm_mode !== 'agency' || !summary.alert_crossed) return null;
  if (dismissed === summary.period_start) return null;

  const currency = summary.currency || locale.currency;
  const money = (cents: number) =>
    new Intl.NumberFormat(locale.locale, { style: 'currency', currency, maximumFractionDigits: 2 }).format(cents / 100);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, summary.period_start);
    } catch {
      /* private mode: banner just comes back next load */
    }
    setDismissed(summary.period_start);
  };

  return (
    <Alert className="m-4 border-warning/40 bg-warning/5">
      <Gauge className="h-4 w-4 text-warning" />
      <AlertTitle>Call tracking usage passed your {money(summary.threshold_cents)} alert</AlertTitle>
      <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
        <span>
          {money(summary.total_cents)} so far this month, heading for about {money(summary.projected_cents)}.
          Nothing stops; it lands as one line on your invoice.
        </span>
        <span className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/settings?tab=usage')}>See usage</Button>
          <Button variant="ghost" size="sm" onClick={dismiss}>Dismiss</Button>
        </span>
      </AlertDescription>
    </Alert>
  );
}
